import { Box } from '@chakra-ui/react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface PageProps {
  children: ReactNode;
  bg: string;
  borderRadius?: string;
  boxShadow?: string;
  width: number;
  height: number;
  zoom: number;
  className?: string;
  border?: boolean;
  testId?: string;
  overflow?: string;
  tabIndex?: number;
  focusElementId?: string;
  focusElementTestId?: string;
  pageRef?: React.RefObject<HTMLDivElement> | null;
  pageSizeRef?: React.RefObject<HTMLDivElement> | null;
  pageScaleRef?: React.RefObject<HTMLDivElement> | null;
}

// Only used in export view when focusElementTestId from parent component is null
const bgContainerTestId = 'bg-wrapper';

export const Page = ({
  children,
  bg,
  borderRadius,
  boxShadow,
  width,
  height,
  zoom,
  className,
  border,
  testId,
  overflow = '',
  tabIndex,
  focusElementId,
  focusElementTestId = bgContainerTestId,
  pageRef,
  pageSizeRef,
  pageScaleRef,
}: PageProps) => {
  const { t } = useTranslation('app', { useSuspense: false });
  const scaledWidth = width * zoom + 'px';
  const scaledHeight = height * zoom + 'px';
  const widthPx = width + 'px';
  const heightPx = height + 'px';
  const isFocusable = typeof tabIndex === 'number';
  const boxBorderStyle = border ? { border: '2px solid black' } : {};

  return (
    <Box data-testid={testId} className={className} width={scaledWidth} height={scaledHeight} m='0' ref={pageSizeRef}>
      <Box
        overflow={overflow}
        height={heightPx}
        width={widthPx}
        transform={`scale(${zoom})`}
        transformOrigin='0 0'
        ref={pageScaleRef}
        style={{ ...boxBorderStyle }}
      >
        <Box
          height={heightPx}
          width={widthPx}
          borderRadius={borderRadius ?? 'sm'}
          boxShadow={boxShadow ?? 'lg'}
          bg={bg}
          tabIndex={tabIndex}
          id={focusElementId}
          _focus={isFocusable ? { boxShadow: 'var(--vg-shadows-outline)' } : {}}
          data-testid={focusElementTestId}
          ref={pageRef}
          aria-label={t('page.ariaLabel')}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};
